<template>
  <v-dialog
    :value="enableDialog"
    persistent
    width="1300"
    @keydown.esc="closeButtonClickEvent"
    @close="closeButtonClickEvent"
  >
    <v-card>
      <v-toolbar flat>
        <v-toolbar-title>複写する売上を選択</v-toolbar-title>
      </v-toolbar>
      <v-divider />
      <v-card-text>

        <!-- 取引番号, 取引日, 終了日 -->
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="conditions.deal_no"
              dense
              hide-details="auto"
              label="取引番号"
              outlined
              clearable
              persistent-hint
              persistent-placeholder
              rounded
              style="font-size: 90%"
            >
            </v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <cs-date-picker
              :date.sync="conditions.trading_start_date"
              @dateChangedCallback="tradingStartDateChangedCallback"
            >
              <template v-slot="{ on, attrs }">
                <v-text-field
                  v-model="conditions.trading_start_date"
                  v-bind="attrs"
                  v-on="on"
                  append-icon="mdi-calendar"
                  clearable
                  dense
                  hide-details="auto"
                  label="取引日"
                  outlined
                  persistent-placeholder
                  placeholder="日付を選択"
                  readonly
                  rounded
                  style="font-size: 90%"
                  @click:clear="tradingStarDateClearEvent"
                >
                  <template v-slot:append-outer>
                    <div
                      v-if="$vuetify.breakpoint.mdAndUp"
                      style="vertical-align: middle"
                    >
                      〜
                    </div>
                  </template>
                </v-text-field>
              </template>
            </cs-date-picker>
          </v-col>
          <v-col cols="12" md="3">
            <cs-date-picker
              :date.sync="conditions.trading_end_date"
              @dateChangedCallback="tradingEndDateChangedCallback"
            >
              <template v-slot="{ on, attrs }">
                <v-text-field
                  v-model="conditions.trading_end_date"
                  v-bind="attrs"
                  v-on="on"
                  append-icon="mdi-calendar"
                  clearable
                  dense
                  hide-details="auto"
                  label="終了日"
                  outlined
                  persistent-placeholder
                  placeholder="日付を選択"
                  readonly
                  rounded
                  style="font-size: 90%"
                  @click:clear="tradingEndDateClearEvent"
                />
              </template>
            </cs-date-picker>
          </v-col>
        </v-row>
        <!-- /取引番号, 取引日, 終了日 -->

        <!-- 取引先(排出)事業者, 取引先(排出)事業場 -->
        <infinite-emission-and-location-auto-complete
          :supplier-id="conditions.supplier_id"
          :location-id="conditions.supplier_location_id"
          :location-code="conditions.supplier_location_code"
          :without-invisible="true"
          supplier-label="取引先（排出）"
          location-label="取引先事業場"
          supplier-md="6"
          location-md="6"
          rounded
          uncontrol-disabled
          classes="search-font"
          @update:values="supplierChangedCallback"
        >
          <template v-slot:supplier-append-outer>
            <choose-emission-button
              :small-button="true"
              :without-invisible="true"
              @choseEmissionCallback="choseSupplierCallback"
            />
          </template>
        </infinite-emission-and-location-auto-complete>
        <!-- /取引先(排出)事業者, 取引先(排出)事業場 -->

        <!-- 取引先(収運・処分)事業者, 取引先(収運・処分)事業場 -->
        <infinite-trader-and-location-auto-complete
          :trader-id="conditions.supplier_trader_id"
          :location-id="conditions.supplier_trader_location_id"
          :location-code="conditions.supplier_trader_location_code"
          :without-invisible="true"
          classes="search-font"
          trader-label="取引先（収運・処分）"
          location-label="取引先事業場"
          trader-md="6"
          location-md="6"
          rounded
          @update:values="supplierTraderChangedCallback"
        >
          <template v-slot:trader-append-outer>
            <choose-trader-button
              :button-caption="`取引先を選択`"
              small-button
              @choseTraderCallback="choseSupplierTraderCallback"
            />
          </template>
        </infinite-trader-and-location-auto-complete>
        <!-- /取引先(収運・処分)事業者, 取引先(収運・処分)事業場 -->

        <!-- 請求先 -->
        <v-row>
          <v-col cols="12" md="6">
            <infinite-billing-auto-complete
              :billing-id="conditions.billing_id"
              rounded
              classes="search-font"
              label="請求先"
              @update:values="billingChangedCallback"
            >
              <template v-slot:append-outer>
                <choose-billing-address-button
                  :small-button="true"
                  @choseBillingAddressCallback="choseBillingCallback"
                />
              </template>
            </infinite-billing-auto-complete>
          </v-col>
        </v-row>
        <!-- /請求先 -->

      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          @click="searchButtonClickEvent"
        >
          <v-icon>mdi-magnify</v-icon>&nbsp;検索
        </v-btn>
      </v-card-actions>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <div>
              <v-input
                v-model="selectedTransactionId"
                dense
                outlined
                readonly
              />
              <v-data-table
                :headers="tableHeaders"
                :items="deals"
                :items-per-page="-1"
                fixed-header
                dense
                height="35vh"
                hide-default-footer
                single-select
                disable-sort
                @click:row="rowClickEvent"
              >
                <template v-slot:[`item.deal_no`]="{ item }">
                  <div>
                    <span v-if="item.deal_no">
                      {{ item.deal_no }}
                    </span>
                    <span v-else>
                      -&nbsp;
                    </span>
                  </div>
                  <div>
                    <span v-if="item.order_no">
                      {{ item.order_no }}
                    </span>
                    <span v-else>
                      -
                    </span>
                  </div>
                </template>
                <template v-slot:[`item.trading_date`]="{item}">
                  <small>{{ item.trading_date | date_format }}</small>
                </template>
                <template v-slot:[`item.sales_date`]="{item}">
                  <small>{{ item.sales_date | date_format }}</small>
                </template>
                <template v-slot:[`item.order_no`]="{item}">
                  <small>{{ item.order_no }}</small>
                </template>
                <template v-slot:[`item.supplier_name`]="{item}">
                  <div>
                    <small>{{ item.supplier_name | truncate(10) }}</small>
                    <v-tooltip
                      top
                      :open-on-hover="false"
                    >
                      <template #activator="{ on }">
                        <v-btn
                          v-if="item.supplier_name"
                          icon
                          small
                          retain-focus-on-click
                          @click="on.click"
                          @blur="on.blur"
                        >
                          <v-icon small>
                            mdi-map-marker-outline
                          </v-icon>
                        </v-btn>
                      </template>
                      <div
                        v-if="item.supplier_name"
                      >
                        <strong>{{ item.supplier_name }}</strong>
                        <br />
                        <strong>{{ item.supplier_location_name }}</strong>
                        <br />
                        <small>{{ item.supplier_location_address }}</small>
                      </div>
                    </v-tooltip>
                  </div>
                  <div>
                    <small>{{ item.supplier_location_name | truncate(10) }}</small>
                  </div>
                </template>
                <template v-slot:[`item.billing_address`]="{item}">
                  <small>{{ item.billing_address  | truncate(10)}}</small>
                </template>
                <template v-slot:[`item.request_date`]="{item}">
                  <small>{{ item.request_date | date_format }}</small>
                </template>
                <template v-slot:[`item.collected_date`]="{item}">
                  <small>{{ item.collected_date | date_format }}</small>
                </template>
                <template v-slot:[`item.receivable_type`]="{item}">
                  <small>{{ item.receivable_type | receivable_type_label }}</small>
                </template>
                <template v-slot:[`item.total_amount`]="{ item }">
                  <span class="font-weight-black">
                    {{ parseFloat(item.total_amount) | comma_format }}
                  </span>
                </template>
                <template v-slot:[`item.sales_staff`]="{item}">
                  <small>{{ item.sales_staff }}</small>
                </template>
              </v-data-table>
            </div>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-pagination
              v-model="page"
              :length="pagination.total_pages"
              :total-visible="totalVisible"
              circle
              prev-icon="mdi-menu-left"
              next-icon="mdi-menu-right"
              @input="pageButtonClickEvent"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn
          @click="closeButtonClickEvent"
        >
          閉じる
        </v-btn>
        <v-btn
          class="primary"
          :disabled="selectedRow === null"
          @click="registerButtonClickEvent"
        >
          複写して作成
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import ChooseBillingAddressButton from '@/driver/components/ChooseBillingAddressButton'
import InfiniteEmissionAndLocationAutoComplete from "@/driver/components/InfiniteEmissionAndLocationAutoComplete.vue";
import ChooseEmissionButton from "@/driver/components/ChooseEmissionButton.vue";
import InfiniteTraderAndLocationAutoComplete from "@/driver/components/InfiniteTraderAndLocationAutoComplete.vue";
import ChooseTraderButton from "@/driver/components/ChooseTraderButton.vue";
import InfiniteBillingAutoComplete from "@/driver/components/InfiniteBillingAutoComplete.vue";

export default {
  name: 'DuplicateDialog',
  components: {
    InfiniteBillingAutoComplete,
    ChooseTraderButton, InfiniteTraderAndLocationAutoComplete,
    ChooseEmissionButton,
    InfiniteEmissionAndLocationAutoComplete,
    ChooseBillingAddressButton,
  },
  mixins: [],
  props: {
    enableDialog: {
      type: Boolean,
      default: () => false
    }
  },
  data() {
    return {
      page: 1,
      totalVisible: 7,
      locations: [],
      pagination: {
        current_page: 1,
        total_count: 0
      },
      selectedRow: null,
      conditions: {
        deal_no: null,
        trading_start_date: null,
        trading_end_date: null,
        /* 取引先(排出)事業者ID */
        supplier_id: null,
        /* 取引先(排出)事業者区分 */
        supplier_type: "Emission",
        /* 取引先(排出)事業場ID */
        supplier_location_id: null,
        /* 取引先(排出)事業場コード */
        supplier_location_code: null,
        /* 取引先(収運・処分)事業者ID */
        supplier_trader_id: null,
        /* 取引先(収運・処分)事業者区分 */
        supplier_trader_type: "Trader",
        /* 取引先(収運・処分)事業場ID */
        supplier_trader_location_id: null,
        /* 取引先(収運・処分)事業場コード */
        supplier_trader_location_code: null,
        /* 請求先ID */
        billing_id: null,
      },
      searchForms: {
        deal_no: null,
        tradingStartDate: null,
        tradingEndDate: null,
        supplierId: null,
        supplierType: null,
        supplierName: null,
        supplierLocationKey: null,
        supplierLocationName: null,
        billingAddressId: null,
        billingAddressName: null,
      },
      selectedTransactionId: null,
      tableHeaders: [
        { text: '取引番号\r\n受注番号', value: 'deal_no', align: 'center' },
        { text: '取引日', value: 'trading_date', align: 'center' },
        { text: '売上計上日', value: 'sales_date', align: 'center' },
        { text: '取引先（事業者）\r\n取引先（事業場）', value: 'supplier_name' },
        { text: '請求先', value: 'billing_address' },
        { text: '現掛区分', value: 'receivable_type', align: 'center' },
        { text: '合計金額', value: 'total_amount', align: 'right' },
        { text: '営業担当者', value: 'sales_staff', align: 'center'},
      ],
      deals: [],
    }
  },
  computed: {},
  watch: {
    enableDialog () {
      if (this.selectedRow) {
        this.selectedRow.select(false)
      }
      this.deals = []
      this.page = 1
      this.selectedRow = null
      this.selectedTransactionId = null
      Object.assign(this.$data.conditions, this.$options.data().conditions)
      Object.assign(this.$data.pagination, this.$options.data().pagination)
      this.pagination = { current_page: 1, total_count: 0 }
    },
  },
  created() {},
  methods: {
    ...mapActions('DriverSalesDealDuplicate', {
      fetchDeals: 'index'
    }),
    supplierChangedCallback (emission_id, location_id, location_code) {
      let changed = false;

      if (this.conditions.supplier_id !== emission_id) {
        changed = true;
      }

      if (this.conditions.supplier_location_id !== location_id) {
        changed = true;
      }

      this.conditions.supplier_id = emission_id;
      this.conditions.supplier_location_id = location_id;
      this.conditions.supplier_location_code = location_code;
      if (changed) {
        this.$nextTick(() => { this.searchTransactions(); })
      }
    },
    /**
     * 取引先選択ボタンからのダイアログのコールバック
     * @param item
     */
    choseSupplierCallback (item) {
      this.conditions.supplier_id = item.id;
      this.conditions.supplier_location_id = null;
      this.conditions.supplier_location_code = null;
      this.$nextTick(() => { this.searchTransactions(); })
    },
    supplierTraderChangedCallback (trader_id, location_id, location_code) {

      let changed = false;

      if (this.conditions.supplier_trader_id !== trader_id) {
        changed = true;
      }

      if (this.conditions.supplier_trader_location_id !== location_id) {
        changed = true;
      }

      this.conditions.supplier_trader_id = trader_id;
      this.conditions.supplier_trader_location_id = location_id;
      this.conditions.supplier_trader_location_code = location_code;

      if (changed) {
        this.$nextTick(() => { this.searchTransactions(); })
      }
    },
    choseSupplierTraderCallback (item) {
      this.conditions.supplier_trader_id = item.id;
      this.conditions.supplier_trader_location_id = null;
      this.conditions.supplier_trader_location_code = null;
      this.$nextTick(() => { this.searchTransactions(); })
    },

    billingChangedCallback (billing_id) {

      let changed = false;

      if (this.conditions.billing_id !== billing_id) {
        changed = true;
      }

      this.conditions.billing_id = billing_id;

      if (changed) {
        this.$nextTick(() => { this.searchTransactions(); })
      }

    },
    /**
     * 請求先選択ボタンからのダイアログのコールバック
     * @param item
     */
    choseBillingCallback (item) {
      this.conditions.billing_id = item.id;
      this.$nextTick(() => { this.searchTransactions(); })
    },
    rowClickEvent (item, row) {
      this.selectedTransactionId = item.id
      this.selectedRow = row
      row.select(true)
    },
    /**
     * 検索ボタン クリックイベント
     */
    searchButtonClickEvent () {
      this.selectedTransactionId = null
      if (this.selectedRow) {
        this.selectedRow.select(false)
        this.selectedRow = null
      }
      this.page = 1
      this.searchTransactions()
    },
    /**
     * ページボタン クリックイベント
     */
    pageButtonClickEvent () {
      this.searchTransactions()
    },
    /**
     * 閉じるボタン クリックイベント
     */
    closeButtonClickEvent () {
      this.$emit('closeCallback')
    },
    /**
     * 複写して作成ボタン クリックイベント
     */
    registerButtonClickEvent () {
      this.$emit('registerCallback', this.selectedTransactionId)
    },

    searchTransactions () {
      let params = {
        ...this.conditions,
        page: this.page,
      }
      this.fetchDeals({ params: params }).then((content) => {
        this.deals = content.deals
        this.pagination = content.pagination
      })
    },
    /**
     * 取引開始日変更　コールバック
     */
    tradingStartDateChangedCallback (value) {
      this.conditions.trading_start_date = value;
      this.searchTransactions();
    },
    /**
     * 取引終了日変更　コールバック
     */
    tradingEndDateChangedCallback (value) {
      this.conditions.trading_end_date = value;
      this.searchTransactions();
    },
    tradingStarDateClearEvent () {
      this.conditions.trading_start_date = null;
      this.searchTransactions();
    },
    tradingEndDateClearEvent () {
      this.conditions.trading_end_date = null;
      this.searchTransactions();
    },
  }
}
</script>

<style scoped lang="scss">

</style>
